import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { reportClientError } from "@/lib/reportError";

// Clean up any stale service worker / cache left by a previous site on this
// domain (e.g. the old Bluehost WordPress install). A lingering SW can
// intercept client-side navigations and serve blank/cached responses.
if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations()
    .then((regs) => regs.forEach((r) => r.unregister()))
    .catch(() => {});
}
if (typeof window !== "undefined" && window.caches && window.caches.keys) {
  window.caches.keys().then((keys) => keys.forEach((k) => window.caches.delete(k))).catch(() => {});
}

// Self-heal: if a stale cached index.html references a JS asset that no longer
// exists after a redeploy, the chunk/script load fails and the app blanks.
// Force a one-time hard reload to fetch the fresh index.html + assets.
window.addEventListener("error", (e) => {
  const msg = (e && (e.message || (e.error && e.error.message))) || "";
  const isChunk = /ChunkLoadError|Loading chunk|Loading CSS chunk|Importing a module script failed/i.test(msg);
  const isAsset = e && e.target && (e.target.tagName === "SCRIPT" || e.target.tagName === "LINK");
  if ((isChunk || isAsset) && !sessionStorage.getItem("jero-reloaded")) {
    sessionStorage.setItem("jero-reloaded", "1");
    window.location.reload();
  }
}, true);
window.addEventListener("load", () => { setTimeout(() => sessionStorage.removeItem("jero-reloaded"), 5000); });

window.addEventListener("error", (e) => { if (e && e.error) reportClientError("window.error", e.error); });
window.addEventListener("unhandledrejection", (e) => {
  if (/zoid destroyed/i.test(String(e?.reason?.message || e?.reason || ""))) { e.preventDefault(); return; }
  reportClientError("unhandledrejection", e && e.reason);
});

// Browser translators/extensions move DOM nodes React owns; stop them crashing the whole app.
if (typeof Node === "function" && Node.prototype) {
  const removeChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) return child;
    return removeChild.call(this, child);
  };
  const insertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, ref) {
    if (ref && ref.parentNode !== this) return newNode;
    return insertBefore.call(this, newNode, ref);
  };
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
